import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Button, Alert, IconButton, Grid, Container, Stack, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, Scatter } from 'recharts';
import { fetchJson } from './api';

const formatPercent = value => Number.isFinite(value) ? `${value >= 0 ? '+' : ''}${value.toFixed(2)}%` : '-';
const formatMoney = value => Number.isFinite(value) ? `$${value.toFixed(2)}` : '--';
const formatOptionMetric = value => Number.isFinite(value) ? value.toLocaleString() : '-';

const CustomShape = (props) => {
    const { cx, cy, fill, payload } = props;
    if (cx == null || cy == null) return null;
    
    // Triangle pointing up for Buy
    if (payload.buy_point !== undefined && payload.buy_point !== null) {
        return (
            <path d={`M${cx},${cy - 10} L${cx - 8},${cy + 6} L${cx + 8},${cy + 6} Z`} fill={fill} />
        );
    }
    // Triangle pointing down for Sell
    if (payload.sell_point !== undefined && payload.sell_point !== null) {
        return (
            <path d={`M${cx},${cy + 10} L${cx - 8},${cy - 6} L${cx + 8},${cy - 6} Z`} fill={fill} />
        );
    }
    return null;
};

export default function StocksSection() {
    const [stocks, setStocks] = useState([]);
    const [query, setQuery] = useState("");
    const [queryResult, setQueryResult] = useState(null);
    const [error, setError] = useState(null);
    const [newTicker, setNewTicker] = useState("");
    const [newName, setNewName] = useState("");
    const [addLoading, setAddLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [signals, setSignals] = useState({});
    const [loadingSignals, setLoadingSignals] = useState(false);
    const [portfolio, setPortfolio] = useState({ balance: null, holdings: [], options: [], activity: [] });
    const [portfolioError, setPortfolioError] = useState(null);
    const [tradeLoading, setTradeLoading] = useState(false);
    const [tradeMessage, setTradeMessage] = useState(null);
    const [tradeAmount, setTradeAmount] = useState({});
    const [tradeMode, setTradeMode] = useState('stock');
    const [equityOrder, setEquityOrder] = useState({ ticker: '', quantity: '1' });
    const [optionOrder, setOptionOrder] = useState({
        ticker: '', expiration: '', strike: '', option_type: 'call', quantity: '1'
    });
    const [optionsChain, setOptionsChain] = useState(null);
    const [chainLoading, setChainLoading] = useState(false);
    const [chainError, setChainError] = useState(null);
    const optionTicketRef = useRef(null);
    const chainRequest = useRef(0);
    const [selectedStock, setSelectedStock] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [loadingChart, setLoadingChart] = useState(false);
    const [chartError, setChartError] = useState(null);
    const chartRequest = useRef(0);

    // Simulation State
    const [simTicker, setSimTicker] = useState("");
    const [simType, setSimType] = useState("buy_daily");
    const [simPeriod, setSimPeriod] = useState("1y");
    const [simResult, setSimResult] = useState(null);
    const [simLoading, setSimLoading] = useState(false);

    const runSimulation = async () => {
        if (!simTicker) return;
        setSimLoading(true);
        setSimResult(null);
        setError(null);
        try {
            const data = await fetchJson('/stocks/simulate', {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ticker: simTicker, type: simType, period: simPeriod })
            });
            setSimResult({ ...data, ticker: simTicker });
        } catch (err) {
            setError(err.message || "Simulation failed");
        } finally {
            setSimLoading(false);
        }
    };

    const openStockChart = async (ticker) => {
        const requestId = ++chartRequest.current;
        setSelectedStock(ticker);
        setLoadingChart(true);
        setChartData([]);
        setChartError(null);
        try {
            const data = await fetchJson(`/stocks/history/${encodeURIComponent(ticker)}`);
            if (requestId === chartRequest.current) setChartData(data);
        } catch (err) {
            if (requestId === chartRequest.current) setChartError(err.message || "Failed to load chart data");
        } finally {
            if (requestId === chartRequest.current) setLoadingChart(false);
        }
    };

    const closeStockChart = () => {
        chartRequest.current += 1;
        setSelectedStock(null);
        setChartData([]);
        setChartError(null);
    };

    const fetchPortfolio = useCallback(async () => {
        try {
            const data = await fetchJson('/stocks/portfolio');
            setPortfolio(data);
            setPortfolioError(null);
        } catch (err) {
            setPortfolioError(err.message || "Could not load the portfolio");
        }
    }, []);

    const loadOptionsChain = useCallback(async (ticker, expiration = '') => {
        if (!ticker) {
            setOptionsChain(null);
            setChainError(null);
            return;
        }
        const requestId = ++chainRequest.current;
        setChainLoading(true);
        setChainError(null);
        try {
            const query = expiration ? `?expiration=${encodeURIComponent(expiration)}` : '';
            const data = await fetchJson(`/stocks/options/${encodeURIComponent(ticker)}${query}`);
            if (requestId !== chainRequest.current) return;
            setOptionsChain(data);
            setOptionOrder(previous => {
                if (previous.ticker !== ticker) return previous;
                const strikeAvailable = data.rows?.some(row => row.strike === Number(previous.strike));
                return {
                    ...previous,
                    expiration: data.expiration,
                    strike: strikeAvailable ? previous.strike : String(data.default_strike),
                };
            });
        } catch (err) {
            if (requestId === chainRequest.current) {
                setOptionsChain(null);
                setChainError(err.message || 'Could not load the options chain');
            }
        } finally {
            if (requestId === chainRequest.current) setChainLoading(false);
        }
    }, []);

    const executeTrade = async (ticker, type, requestedQuantity) => {
        if (tradeLoading) return;
        const quantity = Number(requestedQuantity ?? tradeAmount[ticker]);
        if (!Number.isSafeInteger(quantity) || quantity <= 0) {
            setError('Enter a positive whole-number quantity.');
            return;
        }
        setTradeLoading(true);
        setError(null);
        setTradeMessage(null);

        try {
            const data = await fetchJson(`/stocks/trade/${type}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ticker, quantity })
            });
            setTradeAmount(previous => ({ ...previous, [ticker]: "" }));
            if (requestedQuantity != null) {
                setEquityOrder(previous => ({ ...previous, quantity: '1' }));
            }
            setPortfolio(data.portfolio);
            setPortfolioError(null);
            setTradeMessage(data.message);
        } catch (err) {
            setError(err.message);
        } finally {
            setTradeLoading(false);
        }
    };

    const prepareOptionTrade = (ticker) => {
        setTradeMode('option');
        setOptionOrder(previous => ({ ...previous, ticker }));
        if (typeof optionTicketRef.current?.scrollIntoView === 'function') {
            optionTicketRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const changeOptionExpiration = (expiration) => {
        setOptionOrder(previous => ({ ...previous, expiration, strike: '' }));
        loadOptionsChain(optionOrder.ticker, expiration);
    };

    const selectOptionContract = (strike, optionType) => {
        setOptionOrder(previous => ({
            ...previous,
            strike: String(strike),
            option_type: optionType,
        }));
    };

    const executeOptionTrade = async (type) => {
        if (tradeLoading) return;
        const quantity = Number(optionOrder.quantity);
        const strike = Number(optionOrder.strike);
        if (!optionOrder.ticker.trim() || !optionOrder.expiration || !Number.isFinite(strike) ||
            strike <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) {
            setError('Complete the option ticket with a ticker, expiration, positive strike, and whole-number contracts.');
            return;
        }
        setTradeLoading(true);
        setError(null);
        setTradeMessage(null);
        try {
            const data = await fetchJson(`/stocks/trade/${type}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    asset_type: 'option',
                    ticker: optionOrder.ticker.trim().toUpperCase(),
                    expiration: optionOrder.expiration,
                    strike,
                    option_type: optionOrder.option_type,
                    quantity
                })
            });
            setPortfolio(data.portfolio);
            setPortfolioError(null);
            setTradeMessage(data.message);
        } catch (err) {
            setError(err.message);
        } finally {
            setTradeLoading(false);
        }
    };

    const analyzeAllStocks = async () => {
        setLoadingSignals(true);
        setError(null);
        const newSignals = {};
        const failures = [];
        
        // Process sequentially to avoid overwhelming the backend/yfinance
        for (const stock of stocks) {
            try {
                newSignals[stock.ticker] = await fetchJson(`/stocks/analyze/${encodeURIComponent(stock.ticker)}`);
            } catch (err) {
                failures.push(`${stock.ticker}: ${err.message}`);
            }
        }
        setSignals(newSignals);
        if (failures.length) setError(failures.join('; '));
        setLoadingSignals(false);
    };

    const refreshStocks = async () => {
        setIsRefreshing(true);
        setError(null);
        try {
            const data = await fetchJson('/stocks/refresh', {
                method: "POST"
            });
            if (Array.isArray(data.stocks)) setStocks(data.stocks);
            else await fetchStocks();
            await fetchPortfolio();
        } catch (err) {
            setError(err.message || "Failed to refresh stocks");
        } finally {
            setIsRefreshing(false);
        }
    };

    const fetchStocks = useCallback(async () => {
        try {
            const data = await fetchJson('/stocks');
            if (!Array.isArray(data)) throw new Error('The server returned an invalid watchlist.');
            setStocks(data);
            if (data.length) {
                setEquityOrder(previous => previous.ticker ? previous : { ...previous, ticker: data[0].ticker });
                setOptionOrder(previous => previous.ticker ? previous : { ...previous, ticker: data[0].ticker });
            }
            setError(null);
        } catch (err) {
            setError(err.message || "Could not load stocks");
        }
    }, []);

    useEffect(() => {
        fetchStocks();
        fetchPortfolio();
        return () => {
            chartRequest.current += 1;
            chainRequest.current += 1;
        };
    }, [fetchStocks, fetchPortfolio]);

    useEffect(() => {
        loadOptionsChain(optionOrder.ticker);
    }, [optionOrder.ticker, loadOptionsChain]);

    const addStock = async () => {
        if (!newTicker.trim() || !newName.trim()) {
            setError("Please provide both Ticker and Name");
            return;
        }
        setAddLoading(true);
        setError(null);
        try {
            await fetchJson('/stocks', {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ticker: newTicker.trim().toUpperCase(), name: newName.trim() })
            });
            
            setNewTicker("");
            setNewName("");
            fetchStocks(); // Refresh list
        } catch (err) {
            setError(err.message);
        } finally {
            setAddLoading(false);
        }
    };

    const deleteStock = async (ticker) => {
        if (!window.confirm(`Are you sure you want to delete ${ticker}?`)) return;
        try {
            await fetchJson(`/stocks/quote/${encodeURIComponent(ticker)}`, {
                method: "DELETE"
            });
            fetchStocks(); // Refresh list
        } catch (err) {
            setError(err.message);
        }
    };

    const runQuery = async () => {
        try {
            setQueryResult(null);
            setError(null);
            const data = await fetchJson('/stocks/query', {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ query })
            });
            setQueryResult(data);
        } catch (err) {
            setError(err.message);
        }
    };

    const selectedEquity = stocks.find(stock => stock.ticker === equityOrder.ticker);
    const selectedOptionRow = optionsChain?.rows?.find(row => row.strike === Number(optionOrder.strike));
    const selectedOption = selectedOptionRow?.[optionOrder.option_type];
    const mobileOptionRows = optionsChain?.rows
        ? [...optionsChain.rows]
            .sort((left, right) =>
                Math.abs(left.strike - optionsChain.default_strike) - Math.abs(right.strike - optionsChain.default_strike))
            .slice(0, 7)
            .sort((left, right) => left.strike - right.strike)
        : [];
    const openPositions = [...(portfolio.holdings || []), ...(portfolio.options || [])];

    return (
        <Box sx={{ py: { xs: 3, md: 8 }, minHeight: '100vh', background: 'var(--white)' }}>
            <Container maxWidth="lg">
                <Typography variant="h3" fontWeight={800} sx={{ color: 'var(--rosewood)', mb: 1, fontSize: { xs: '2rem', md: '3rem' } }}>
                    Practice Trading
                </Typography>
                <Typography variant="h6" sx={{ color: 'var(--chinese-violet)', mb: 3, fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    Learn with pretend money. Nothing here buys a real stock or option.
                    {isRefreshing && <span style={{ fontSize: '0.8em', color: 'var(--pacific-cyan)', marginLeft: '10px' }}>(Updating prices...)</span>}
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                {stocks.some(stock => stock.quote_error) && <Alert severity="warning" sx={{ mb: 3 }}>
                    Some quotes are unavailable: {stocks.filter(stock => stock.quote_error).map(stock => `${stock.ticker}: ${stock.quote_error}`).join('; ')}.
                    Watchlist entries remain available for management.
                </Alert>}
                {tradeMessage && <Alert severity="success" sx={{ mb: 3 }}>{tradeMessage}</Alert>}
                <Alert role="status" severity="info" sx={{ mb: 3, '& .MuiAlert-message': { fontSize: { xs: '0.78rem', sm: '0.875rem' } } }}>
                    Prices and completed daily bars are from <a href="https://finance.yahoo.com/" target="_blank" rel="noopener noreferrer">Yahoo Finance</a>, in USD, and may be delayed.
                    Technical signals are rules-based, not AI predictions or investment advice.
                    {' '}This shared paper account and its fills are persisted in the site database and use the main site access gate.
                </Alert>

                <Paper elevation={0} sx={{
                    p: { xs: 2.5, sm: 3.5 }, mb: 3, borderRadius: 4,
                    color: '#fff', background: 'linear-gradient(135deg, #6b141d 0%, #a11e2b 100%)'
                }}>
                    <Typography variant="overline" fontWeight={800}>Your pretend account</Typography>
                    <Typography variant="h3" fontWeight={900} sx={{ fontSize: { xs: '2.35rem', sm: '3rem' } }}>
                        {formatMoney(portfolio.equity)}
                    </Typography>
                    <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
                        <Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Money left to spend</Typography>
                            <Typography variant="h6" fontWeight={800}>{formatMoney(portfolio.buying_power ?? portfolio.balance)}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Profit or loss</Typography>
                            <Typography variant="h6" fontWeight={800}>{formatMoney(portfolio.total_pnl)}</Typography>
                        </Box>
                    </Stack>
                </Paper>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {[
                        ['Cash', portfolio.balance],
                        ['Market Value', portfolio.market_value],
                        ['Starting Balance', portfolio.starting_balance],
                        ['Realized P&L', portfolio.realized_pnl],
                        ['Unrealized P&L', portfolio.unrealized_pnl],
                        ['Total P&L', portfolio.total_pnl],
                    ].map(([label, value]) => (
                        <Grid key={label} size={{ xs: 6, md: 3 }}>
                            <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, height: '100%', border: '1px solid rgba(131, 107, 137, 0.2)', borderRadius: 3 }}>
                                <Typography variant="caption" sx={{ color: 'var(--chinese-violet)', fontWeight: 700 }}>{label}</Typography>
                                <Typography variant="h5" fontWeight={800} sx={{
                                    mt: 0.5,
                                    fontSize: { xs: '1.1rem', sm: '1.5rem' },
                                    color: label.includes('P&L') && Number.isFinite(value)
                                        ? (value >= 0 ? '#2e7d32' : '#d32f2f')
                                        : 'var(--black)'
                                }}>
                                    {formatMoney(value)}
                                </Typography>
                                {label === 'Total P&L' && Number.isFinite(portfolio.total_return_percent) && (
                                    <Typography variant="caption" sx={{ color: value >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                        {formatPercent(portfolio.total_return_percent)} from {formatMoney(portfolio.starting_balance)}
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ order: 3 }}>
                <Box className="ticker-wrap">
                    <div className="ticker-content">
                        {stocks.map((stock) => (
                            <div className="ticker-item" key={stock.ticker}>
                                <span style={{ color: 'var(--rosewood)', marginRight: '8px' }}>{stock.ticker}</span>
                                <span style={{ color: 'var(--black)' }}>${stock.current_price?.toFixed(2) || '---'}</span>
                                <span style={{
                                    color: (stock.change_percent || 0) >= 0 ? 'var(--pacific-cyan)' : 'var(--crimson)',
                                    marginLeft: '8px',
                                    fontSize: '0.9em'
                                }}>
                                    {formatPercent(stock.change_percent)}
                                </span>
                            </div>
                        ))}
                    </div>
                </Box>

                {/* Section 1: Market Overview */}
                <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--black)', mb: 3, mt: 4 }}>
                    Market Overview
                </Typography>
                <Grid container spacing={4}>
                    {/* Watchlist */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Paper elevation={0} sx={{ 
                            p: 0, 
                            borderRadius: 4, 
                            border: '1px solid rgba(0,0,0,0.05)',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--black)' }}>
                                    Sid's watchlist
                                </Typography>
                                <Box>
                                    <Button 
                                        startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                                        onClick={refreshStocks}
                                        disabled={isRefreshing}
                                        sx={{ color: 'var(--chinese-violet)', fontWeight: 600, mr: 2 }}
                                    >
                                        {isRefreshing ? "Updating..." : "Refresh"}
                                    </Button>
                                </Box>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ background: 'rgba(165, 209, 225, 0.1)' }}>
                                            <TableCell sx={{ fontWeight: 600, color: 'var(--chinese-violet)' }}>Ticker</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'var(--chinese-violet)' }}>Name</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--chinese-violet)' }}>Price</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--chinese-violet)' }}>Change</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--chinese-violet)' }}>YTD</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--chinese-violet)' }}>Fwd PE</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--chinese-violet)' }}>Volume</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--chinese-violet)' }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stocks.map((stock) => (
                                            <TableRow key={stock.ticker} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell component="th" scope="row" sx={{ fontWeight: 700, color: 'var(--rosewood)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openStockChart(stock.ticker)}>
                                                    {stock.ticker}
                                                </TableCell>
                                                <TableCell sx={{ color: 'var(--black)' }}>{stock.name}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }} title={stock.as_of ? `${stock.source || 'Yahoo Finance'}; last completed close: ${stock.close_as_of || 'unavailable'}` : undefined}>
                                                    ${stock.current_price?.toFixed(2) || '-'}
                                                    {stock.as_of && <Typography component="div" variant="caption" color="text.secondary">{new Date(stock.as_of).toLocaleString()}</Typography>}
                                                </TableCell>
                                                <TableCell align="right" sx={{ 
                                                    color: (stock.change_percent || 0) >= 0 ? 'var(--pacific-cyan)' : 'var(--crimson)',
                                                    fontWeight: 600
                                                }}>
                                                    {formatPercent(stock.change_percent)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ 
                                                    color: (stock.ytd_change || 0) >= 0 ? 'var(--pacific-cyan)' : 'var(--crimson)',
                                                    fontWeight: 600
                                                }}>
                                                    {formatPercent(stock.ytd_change)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {stock.forward_pe?.toFixed(2) || '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {Number.isFinite(stock.volume) ? (stock.volume >= 1000000 ? `${(stock.volume / 1000000).toFixed(2)}M` : stock.volume.toLocaleString()) : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button size="small" onClick={() => prepareOptionTrade(stock.ticker)}
                                                        aria-label={`Trade ${stock.ticker} options`}
                                                        sx={{ mr: 1, textTransform: 'none', fontWeight: 700 }}>
                                                        Trade option
                                                    </Button>
                                                    <IconButton aria-label={`Delete ${stock.ticker}`} onClick={() => deleteStock(stock.ticker)} size="small" sx={{ color: 'var(--chinese-violet)', '&:hover': { color: 'var(--crimson)' } }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Add Stock */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper id="add-stock-form" elevation={0} sx={{ 
                            p: 3, 
                            borderRadius: 4, 
                            border: '1px solid rgba(0,0,0,0.05)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: 'var(--black)' }}>
                                Add to Watchlist
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    label="Ticker Symbol"
                                    value={newTicker}
                                    onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
                                    size="small"
                                    fullWidth
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <TextField
                                    label="Company Name"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={addStock}
                                    disabled={addLoading}
                                    fullWidth
                                    sx={{
                                        background: 'var(--pacific-cyan)',
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        boxShadow: '0 4px 12px rgba(75, 163, 195, 0.3)',
                                        '&:hover': { background: 'var(--light-blue)' }
                                    }}
                                >
                                    {addLoading ? <CircularProgress size={24} color="inherit" /> : "Add Stock"}
                                </Button>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Section 2: Trading Desk */}
                <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--black)', mb: 3, mt: 6 }}>
                    Trading Desk
                </Typography>
                <Grid container spacing={4}>
                    {/* Signals */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Paper elevation={0} sx={{ 
                            p: 0, 
                            borderRadius: 4, 
                            border: '1px solid rgba(0,0,0,0.05)',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--black)' }}>
                                    Technical Signals (SMA / RSI / MACD)
                                </Typography>
                                <Button 
                                    variant="contained"
                                    onClick={analyzeAllStocks}
                                    disabled={loadingSignals}
                                    sx={{ 
                                        bgcolor: 'var(--pacific-cyan)', 
                                        color: 'white',
                                        fontWeight: 600,
                                        boxShadow: 'none',
                                        '&:hover': { bgcolor: 'var(--rosewood)' }
                                    }}
                                >
                                    {loadingSignals ? "Analyzing..." : "Generate Signals"}
                                </Button>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ background: 'rgba(165, 209, 225, 0.1)' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Signal</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Conf.</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Win Rate</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Sentiment</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Trade</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.keys(signals).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'var(--chinese-violet)' }}>
                                                    Click "Generate Signals" to calculate historical technical indicators
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            stocks.map(stock => {
                                                const signal = signals[stock.ticker];
                                                if (!signal) return null;
                                                return (
                                                    <TableRow key={stock.ticker}>
                                                        <TableCell sx={{ fontWeight: 700 }}>{stock.ticker}</TableCell>
                                                        <TableCell title={`${signal.method || 'Technical indicators'}; as of ${signal.as_of || 'unavailable'}`}>
                                                            <span style={{
                                                                padding: '4px 12px',
                                                                borderRadius: '12px',
                                                                fontWeight: 600,
                                                                fontSize: '0.85rem',
                                                                backgroundColor: signal.signal === 'Buy' ? 'rgba(76, 175, 80, 0.1)' : signal.signal === 'Sell' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                                                                color: signal.signal === 'Buy' ? '#2e7d32' : signal.signal === 'Sell' ? '#d32f2f' : '#616161'
                                                            }}>
                                                                {signal.signal}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>{Number.isFinite(signal.confidence) ? `${(signal.confidence * 100).toFixed(0)}%` : '-'}</TableCell>
                                                        <TableCell>
                                                            {Number.isFinite(signal.win_rate) ? (
                                                                <span style={{ color: signal.win_rate > 0.6 ? 'green' : 'orange' }}>
                                                                    {(signal.win_rate * 100).toFixed(0)}%
                                                                </span>
                                                            ) : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {!Number.isFinite(signal.sentiment) ? '-' : signal.sentiment !== 0 ? (
                                                                <span style={{ color: signal.sentiment > 0 ? 'green' : 'red' }}>
                                                                    {signal.sentiment > 0 ? 'Positive' : 'Negative'} ({signal.sentiment})
                                                                </span>
                                                            ) : 'Neutral'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <TextField 
                                                                    size="small" 
                                                                    placeholder="Qty" 
                                                                    type="number"
                                                                    sx={{ width: '70px' }}
                                                                    value={tradeAmount[stock.ticker] || ''}
                                                                    onChange={(e) => setTradeAmount({...tradeAmount, [stock.ticker]: e.target.value})}
                                                                />
                                                                <Button 
                                                                    size="small" 
                                                                    variant="contained" 
                                                                    color="success"
                                                                    disabled={tradeLoading || !!stock.quote_error}
                                                                    onClick={() => executeTrade(stock.ticker, 'buy')}
                                                                >
                                                                    Buy
                                                                </Button>
                                                                <Button 
                                                                    size="small" 
                                                                    variant="contained" 
                                                                    color="error"
                                                                    disabled={tradeLoading || !!stock.quote_error}
                                                                    onClick={() => executeTrade(stock.ticker, 'sell')}
                                                                >
                                                                    Sell
                                                                </Button>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Portfolio & AI */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Stack spacing={4}>
                            {/* Portfolio Summary */}
                            <Paper elevation={0} sx={{ 
                                p: 3, 
                                borderRadius: 4, 
                                border: '1px solid rgba(0,0,0,0.05)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                background: 'linear-gradient(135deg, var(--rosewood) 0%, #5c3a45 100%)',
                                color: 'white'
                            }}>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, opacity: 0.9 }}>
                                    Account Snapshot
                                </Typography>
                                <Typography variant="h3" fontWeight={800} sx={{ mb: 1 }}>
                                    {formatMoney(portfolio.equity)}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                                    Net Liquidation
                                </Typography>
                                {portfolioError && <Alert severity="warning" sx={{ mb: 2 }}>{portfolioError}</Alert>}
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Cash</span><strong>{formatMoney(portfolio.balance)}</strong></Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Unrealized</span><strong>{formatMoney(portfolio.unrealized_pnl)}</strong></Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Realized</span><strong>{formatMoney(portfolio.realized_pnl)}</strong></Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Open positions</span><strong>{(portfolio.holdings?.length || 0) + (portfolio.options?.length || 0)}</strong></Box>
                                </Stack>
                            </Paper>

                            {/* AI Query */}
                            <Paper elevation={0} sx={{ 
                                p: 3, 
                                borderRadius: 4, 
                                border: '1px solid rgba(0,0,0,0.05)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                background: 'linear-gradient(135deg, var(--white) 0%, rgba(165, 209, 225, 0.1) 100%)'
                            }}>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: 'var(--black)' }}>
                                    Market Data Lookup
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 3, color: 'var(--chinese-violet)' }}>
                                    Look up a watchlist ticker, company name, "top", or "portfolio".
                                </Typography>
                                <TextField
                                    label="Ask a question..."
                                    multiline
                                    rows={3}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    fullWidth
                                    sx={{ 
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'var(--white)' } 
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={runQuery}
                                    startIcon={<QueryStatsIcon />}
                                    fullWidth
                                    sx={{
                                        background: 'var(--rosewood)',
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        '&:hover': { background: 'var(--madder)' }
                                    }}
                                >
                                    Analyze
                                </Button>

                                {queryResult && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'var(--white)', borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)', overflowX: 'auto' }}>
                                        {queryResult.answer && <Typography variant="body2" sx={{ mb: 2, color: 'var(--black)' }}>{queryResult.answer}</Typography>}
                                        {queryResult.columns && queryResult.data ? (
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        {queryResult.columns.map((col) => (
                                                            <TableCell key={col} sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'var(--chinese-violet)' }}>
                                                                {col.replace(/_/g, ' ').toUpperCase()}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {queryResult.data.map((row, i) => (
                                                        <TableRow key={i}>
                                                            {queryResult.columns.map((col) => (
                                                                <TableCell key={col} sx={{ fontSize: '0.75rem' }}>
                                                                    {typeof row[col] === 'number' ? row[col].toFixed(2) : row[col]}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: 'var(--black)' }}>
                                                {!queryResult.answer && JSON.stringify(queryResult)}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        </Stack>
                    </Grid>
                </Grid>
                </Box>

                <Box sx={{ order: 1 }}>
                <Typography variant="h5" fontWeight={800} sx={{ color: 'var(--black)', mb: 1, mt: 2 }}>
                    Quick Trade
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--chinese-violet)', mb: 2 }}>
                    1. Pick what you want to practice. 2. Choose an amount. 3. Tap buy or sell.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button
                        variant={tradeMode === 'stock' ? 'contained' : 'outlined'}
                        onClick={() => setTradeMode('stock')}
                        fullWidth
                        sx={{ minHeight: 52, borderRadius: 3, fontWeight: 800, textTransform: 'none', fontSize: '1rem' }}
                    >
                        Trade stocks
                    </Button>
                    <Button
                        variant={tradeMode === 'option' ? 'contained' : 'outlined'}
                        onClick={() => setTradeMode('option')}
                        fullWidth
                        sx={{ minHeight: 52, borderRadius: 3, fontWeight: 800, textTransform: 'none', fontSize: '1rem' }}
                    >
                        Trade options
                    </Button>
                </Stack>
                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1.5, mb: 1, scrollbarWidth: 'none' }}>
                    {stocks.map(stock => (
                        <Button key={stock.ticker} variant={
                            (tradeMode === 'stock' ? equityOrder.ticker : optionOrder.ticker) === stock.ticker ? 'contained' : 'outlined'
                        } onClick={() => {
                            setEquityOrder(previous => ({ ...previous, ticker: stock.ticker }));
                            setOptionOrder(previous => ({ ...previous, ticker: stock.ticker }));
                        }} sx={{ minWidth: 84, minHeight: 44, borderRadius: 3, fontWeight: 800 }}>
                            {stock.ticker}
                        </Button>
                    ))}
                </Box>

                <Paper elevation={0} sx={{
                    display: tradeMode === 'stock' ? 'block' : 'none',
                    p: { xs: 2, sm: 3 }, borderRadius: 4, border: '2px solid rgba(107,20,29,0.16)', mb: 4
                }}>
                    <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Buy or sell shares</Typography>
                    <Typography variant="body2" sx={{ color: 'var(--chinese-violet)', mb: 2 }}>
                        A share is one small piece of a company.
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 5, md: 4 }}>
                            <TextField select label="Company" value={equityOrder.ticker} fullWidth
                                onChange={(event) => setEquityOrder(previous => ({ ...previous, ticker: event.target.value }))}>
                                {stocks.map(stock => (
                                    <MenuItem key={stock.ticker} value={stock.ticker}>
                                        {stock.ticker} · {stock.name} · {formatMoney(stock.current_price)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                            <TextField label="How many shares?" type="number" value={equityOrder.quantity} fullWidth
                                inputProps={{ min: 1, step: 1 }}
                                onChange={(event) => setEquityOrder(previous => ({ ...previous, quantity: event.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 1 }}>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(75,163,195,0.1)', borderRadius: 2 }}>
                                <Typography variant="caption">About this much</Typography>
                                <Typography fontWeight={800}>
                                    {formatMoney((selectedEquity?.current_price || 0) * Number(equityOrder.quantity || 0))}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button variant="contained" color="success" fullWidth
                                    disabled={tradeLoading || !equityOrder.ticker}
                                    onClick={() => executeTrade(equityOrder.ticker, 'buy', equityOrder.quantity)}
                                    sx={{ minHeight: 54, borderRadius: 3, fontWeight: 900, fontSize: '1rem' }}>Buy shares</Button>
                                <Button variant="contained" color="error" fullWidth
                                    disabled={tradeLoading || !equityOrder.ticker}
                                    onClick={() => executeTrade(equityOrder.ticker, 'sell', equityOrder.quantity)}
                                    sx={{ minHeight: 54, borderRadius: 3, fontWeight: 900, fontSize: '1rem' }}>Sell shares</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper ref={optionTicketRef} elevation={0} sx={{
                    display: tradeMode === 'option' ? 'block' : 'none',
                    p: { xs: 2, sm: 3 }, borderRadius: 4, border: '2px solid rgba(107,20,29,0.16)', mb: 4,
                    scrollMarginTop: 24
                }}>
                    <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Buy or sell an option</Typography>
                    <Typography variant="body2" sx={{ color: 'var(--chinese-violet)', mb: 3 }}>
                        A call practices betting the price goes up. A put practices betting it goes down.
                        One contract controls 100 shares.
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField select label="Company" value={optionOrder.ticker} fullWidth
                                onChange={(event) => setOptionOrder(previous => ({ ...previous, ticker: event.target.value }))}>
                                {stocks.map(stock => (
                                    <MenuItem key={stock.ticker} value={stock.ticker}>{stock.ticker} · {stock.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField select label="Ends on" value={optionOrder.expiration} fullWidth
                                disabled={chainLoading || !optionsChain}
                                onChange={(event) => changeOptionExpiration(event.target.value)}>
                                {(optionsChain?.expirations || []).map(expiration => (
                                    <MenuItem key={expiration} value={expiration}>{expiration}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                            <TextField select label="Target price" value={optionOrder.strike} fullWidth
                                disabled={chainLoading || !optionsChain}
                                onChange={(event) => setOptionOrder(previous => ({ ...previous, strike: event.target.value }))}>
                                {(optionsChain?.rows || []).map(row => (
                                    <MenuItem key={row.strike} value={String(row.strike)}>${row.strike}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id="option-type-label">Direction</InputLabel>
                                <Select labelId="option-type-label" label="Direction" value={optionOrder.option_type}
                                    onChange={(event) => setOptionOrder(previous => ({ ...previous, option_type: event.target.value }))}>
                                    <MenuItem value="call">Call · price goes up</MenuItem>
                                    <MenuItem value="put">Put · price goes down</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 1 }}>
                            <TextField label="How many?" type="number" value={optionOrder.quantity} fullWidth
                                onChange={(event) => setOptionOrder(previous => ({ ...previous, quantity: event.target.value }))} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(75,163,195,0.1)', borderRadius: 2 }}>
                                <Typography variant="caption">About this much</Typography>
                                <Typography fontWeight={800}>
                                    {formatMoney((selectedOption?.last_price || 0) * 100 * Number(optionOrder.quantity || 0))}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button variant="contained" color="success" fullWidth disabled={tradeLoading}
                                    onClick={() => executeOptionTrade('buy')}
                                    sx={{ minHeight: 54, borderRadius: 3, fontWeight: 900 }}>Buy option</Button>
                                <Button variant="contained" color="error" fullWidth disabled={tradeLoading}
                                    onClick={() => executeOptionTrade('sell')}
                                    sx={{ minHeight: 54, borderRadius: 3, fontWeight: 900 }}>Sell option</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3 }}>
                        {chainLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}>
                                <CircularProgress size={22} />
                                <Typography variant="body2">Loading current options chain...</Typography>
                            </Box>
                        )}
                        {chainError && <Alert severity="warning">{chainError}</Alert>}
                        {optionsChain && !chainLoading && (
                            <>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ mb: 1.5 }}>
                                    <Typography variant="subtitle1" fontWeight={800}>
                                        {optionsChain.ticker} chain · underlying {formatMoney(optionsChain.underlying_price)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'var(--chinese-violet)' }}>
                                        {optionsChain.expiration} · {optionsChain.source} · click a call or put to select it
                                    </Typography>
                                </Stack>
                                <Box aria-label={`${optionsChain.ticker} easy options`} sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    <Typography variant="body2" fontWeight={800} sx={{ mb: 1 }}>
                                        Pick a target price
                                    </Typography>
                                    <Stack spacing={1}>
                                        {mobileOptionRows.map(row => (
                                            <Paper key={row.strike} elevation={0} sx={{
                                                p: 1.5, borderRadius: 3,
                                                border: Number(optionOrder.strike) === row.strike
                                                    ? '2px solid var(--pacific-cyan)'
                                                    : '1px solid rgba(0,0,0,0.1)'
                                            }}>
                                                <Typography align="center" fontWeight={900} sx={{ mb: 1 }}>${row.strike} target</Typography>
                                                <Stack direction="row" spacing={1}>
                                                    <Button fullWidth variant={Number(optionOrder.strike) === row.strike && optionOrder.option_type === 'call' ? 'contained' : 'outlined'}
                                                        color="success" disabled={!row.call}
                                                        onClick={() => selectOptionContract(row.strike, 'call')}
                                                        aria-label={`Select ${optionsChain.ticker} ${optionsChain.expiration} ${row.strike} call`}
                                                        sx={{ minHeight: 50, borderRadius: 2.5, textTransform: 'none' }}>
                                                        <Box><strong>Up call</strong><br />{formatMoney(row.call?.last_price)}</Box>
                                                    </Button>
                                                    <Button fullWidth variant={Number(optionOrder.strike) === row.strike && optionOrder.option_type === 'put' ? 'contained' : 'outlined'}
                                                        color="error" disabled={!row.put}
                                                        onClick={() => selectOptionContract(row.strike, 'put')}
                                                        aria-label={`Select ${optionsChain.ticker} ${optionsChain.expiration} ${row.strike} put`}
                                                        sx={{ minHeight: 50, borderRadius: 2.5, textTransform: 'none' }}>
                                                        <Box><strong>Down put</strong><br />{formatMoney(row.put?.last_price)}</Box>
                                                    </Button>
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                    <Typography variant="caption" display="block" sx={{ mt: 1.5, color: 'var(--chinese-violet)' }}>
                                        Showing the 7 target prices closest to today’s price. Use “Target price” above to see every strike.
                                    </Typography>
                                </Box>
                                <TableContainer sx={{ display: { xs: 'none', sm: 'block' }, maxHeight: 440, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
                                    <Table size="small" stickyHeader aria-label={`${optionsChain.ticker} options chain`}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="right" sx={{ width: '42%', fontWeight: 800 }}>Calls</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 800 }}>Strike</TableCell>
                                                <TableCell sx={{ width: '42%', fontWeight: 800 }}>Puts</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {optionsChain.rows.map(row => (
                                                <TableRow key={row.strike} selected={Number(optionOrder.strike) === row.strike}>
                                                    <TableCell align="right">
                                                        {row.call ? (
                                                            <Button size="small" onClick={() => selectOptionContract(row.strike, 'call')}
                                                                aria-label={`Select ${optionsChain.ticker} ${optionsChain.expiration} ${row.strike} call`}
                                                                sx={{ textTransform: 'none', textAlign: 'right' }}>
                                                                <Box>
                                                                    <strong>{formatMoney(row.call.last_price)}</strong>
                                                                    <Typography component="div" variant="caption">
                                                                        {formatMoney(row.call.bid)} × {formatMoney(row.call.ask)} · IV {row.call.implied_volatility == null ? '-' : formatPercent(row.call.implied_volatility * 100)}
                                                                    </Typography>
                                                                    <Typography component="div" variant="caption">
                                                                        Vol {formatOptionMetric(row.call.volume)} · OI {formatOptionMetric(row.call.open_interest)}
                                                                    </Typography>
                                                                </Box>
                                                            </Button>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 800 }}>${row.strike}</TableCell>
                                                    <TableCell>
                                                        {row.put ? (
                                                            <Button size="small" onClick={() => selectOptionContract(row.strike, 'put')}
                                                                aria-label={`Select ${optionsChain.ticker} ${optionsChain.expiration} ${row.strike} put`}
                                                                sx={{ textTransform: 'none', textAlign: 'left' }}>
                                                                <Box>
                                                                    <strong>{formatMoney(row.put.last_price)}</strong>
                                                                    <Typography component="div" variant="caption">
                                                                        {formatMoney(row.put.bid)} × {formatMoney(row.put.ask)} · IV {row.put.implied_volatility == null ? '-' : formatPercent(row.put.implied_volatility * 100)}
                                                                    </Typography>
                                                                    <Typography component="div" variant="caption">
                                                                        Vol {formatOptionMetric(row.put.volume)} · OI {formatOptionMetric(row.put.open_interest)}
                                                                    </Typography>
                                                                </Box>
                                                            </Button>
                                                        ) : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </Box>
                </Paper>

                <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--black)', mb: 3 }}>
                    What you own
                </Typography>
                <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden', mb: 4 }}>
                    <Box sx={{ display: { xs: 'block', sm: 'none' }, p: 2 }}>
                        {openPositions.length === 0 ? (
                            <Typography align="center" sx={{ py: 3 }}>You do not own anything yet. Try a pretend trade above.</Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {openPositions.map(position => (
                                    <Paper key={position.contract_symbol || position.ticker} elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(131,107,137,0.08)' }}>
                                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                                            <Box>
                                                <Typography fontWeight={900}>{position.contract_symbol || position.ticker}</Typography>
                                                <Typography variant="caption">{position.quantity} {position.contract_symbol ? 'contract(s)' : 'share(s)'}</Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography fontWeight={900}>{formatMoney(position.total_value)}</Typography>
                                                <Typography variant="caption" sx={{ color: position.unrealized_pnl >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                                    {formatMoney(position.unrealized_pnl)} profit/loss
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Box>
                    <TableContainer sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Instrument</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">Average</TableCell>
                                    <TableCell align="right">Last</TableCell>
                                    <TableCell align="right">Market Value</TableCell>
                                    <TableCell align="right">Unrealized P&L</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {openPositions.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>No open positions. Your account starts with $1,000 buying power.</TableCell></TableRow>
                                ) : openPositions.map(position => (
                                    <TableRow key={position.contract_symbol || position.ticker}>
                                        <TableCell>
                                            <strong>{position.contract_symbol || position.ticker}</strong>
                                            {position.contract_symbol && <Typography variant="caption" display="block">{position.option_type.toUpperCase()} · {position.expiration} · ${position.strike}</Typography>}
                                        </TableCell>
                                        <TableCell align="right">{position.quantity}</TableCell>
                                        <TableCell align="right">{formatMoney(position.avg_price)}</TableCell>
                                        <TableCell align="right">{formatMoney(position.current_price)}</TableCell>
                                        <TableCell align="right">{formatMoney(position.total_value)}</TableCell>
                                        <TableCell align="right" sx={{ color: position.unrealized_pnl >= 0 ? '#2e7d32' : '#d32f2f', fontWeight: 700 }}>
                                            {formatMoney(position.unrealized_pnl)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--black)', mb: 3 }}>
                    What you did
                </Typography>
                <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden', mb: 4 }}>
                    <Box sx={{ display: { xs: 'block', sm: 'none' }, p: 2 }}>
                        {(portfolio.activity || []).length === 0 ? (
                            <Typography align="center" sx={{ py: 3 }}>No trades yet.</Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {portfolio.activity.map(fill => (
                                    <Paper key={fill.id} elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(131,107,137,0.08)' }}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Box>
                                                <Typography fontWeight={900} sx={{ color: fill.side === 'buy' ? '#2e7d32' : '#d32f2f' }}>
                                                    {fill.side === 'buy' ? 'Bought' : 'Sold'} {fill.symbol}
                                                </Typography>
                                                <Typography variant="caption">{new Date(fill.timestamp).toLocaleString()}</Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography fontWeight={900}>{formatMoney(fill.amount)}</Typography>
                                                <Typography variant="caption">{fill.quantity} at {formatMoney(fill.price)}</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Box>
                    <TableContainer sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Table size="small">
                            <TableHead><TableRow>
                                <TableCell>Time</TableCell><TableCell>Side</TableCell><TableCell>Symbol</TableCell>
                                <TableCell align="right">Qty</TableCell><TableCell align="right">Fill</TableCell>
                                <TableCell align="right">Amount</TableCell><TableCell align="right">Realized P&L</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {(portfolio.activity || []).length === 0 ? (
                                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No fills yet.</TableCell></TableRow>
                                ) : portfolio.activity.map(fill => (
                                    <TableRow key={fill.id}>
                                        <TableCell>{new Date(fill.timestamp).toLocaleString()}</TableCell>
                                        <TableCell sx={{ color: fill.side === 'buy' ? '#2e7d32' : '#d32f2f', fontWeight: 700 }}>{fill.side.toUpperCase()}</TableCell>
                                        <TableCell>{fill.symbol}</TableCell>
                                        <TableCell align="right">{fill.quantity}</TableCell>
                                        <TableCell align="right">{formatMoney(fill.price)}</TableCell>
                                        <TableCell align="right">{formatMoney(fill.amount)}</TableCell>
                                        <TableCell align="right">{fill.realized_pnl ? formatMoney(fill.realized_pnl) : '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                </Box>

                <Box sx={{ order: 2 }}>
                {/* Section 3: Strategy Simulator */}
                <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--black)', mb: 3, mt: 6 }}>
                    Strategy Simulator (Backtest)
                </Typography>
                <Paper elevation={0} sx={{ 
                    p: 4, 
                    borderRadius: 4, 
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="simulation-stock-label">Select Stock</InputLabel>
                                <Select
                                    labelId="simulation-stock-label"
                                    value={simTicker}
                                    label="Select Stock"
                                    onChange={(e) => setSimTicker(e.target.value)}
                                >
                                    {stocks.map((s) => (
                                        <MenuItem key={s.ticker} value={s.ticker}>{s.ticker}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="simulation-strategy-label">Strategy</InputLabel>
                                <Select
                                    labelId="simulation-strategy-label"
                                    value={simType}
                                    label="Strategy"
                                    onChange={(e) => setSimType(e.target.value)}
                                >
                                    <MenuItem value="buy_daily">Long (Buy & Hold 1 Share)</MenuItem>
                                    <MenuItem value="short_daily">Short (Sell & Hold 1 Share)</MenuItem>
                                    <MenuItem value="sma_crossover">SMA 20/50 Crossover</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="simulation-period-label">Period</InputLabel>
                                <Select
                                    labelId="simulation-period-label"
                                    value={simPeriod}
                                    label="Period"
                                    onChange={(e) => setSimPeriod(e.target.value)}
                                >
                                    <MenuItem value="1mo">1 Month</MenuItem>
                                    <MenuItem value="3mo">3 Months</MenuItem>
                                    <MenuItem value="6mo">6 Months</MenuItem>
                                    <MenuItem value="ytd">YTD</MenuItem>
                                    <MenuItem value="1y">1 Year</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Button 
                                variant="contained" 
                                fullWidth 
                                onClick={runSimulation}
                                disabled={simLoading || !simTicker}
                                startIcon={simLoading ? <CircularProgress size={20} color="inherit"/> : <PlayArrowIcon />}
                                sx={{ bgcolor: 'var(--rosewood)', fontWeight: 700 }}
                            >
                                Run Sim
                            </Button>
                        </Grid>
                    </Grid>

                    {simResult && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Historical price-only backtest against VOO. Dividends, fees, taxes, slippage and short-borrow costs are excluded.
                                SMA crossover uses prior-session signals and fractional shares. Past performance does not predict future returns.
                            </Typography>
                            {simResult.method && <Typography variant="caption" component="p" sx={{ mb: 2 }}>
                                {simResult.source}; {simResult.method} Sessions: {simResult.start_date} to {simResult.end_date}.
                                {' '}{simResult.assumptions?.join(' ')}
                            </Typography>}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }} elevation={0}>
                                        <Typography variant="caption" color="text.secondary">Strategy Return</Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: simResult.summary.roi >= 0 ? 'green' : 'red' }}>
                                            {simResult.summary.roi >= 0 ? '+' : ''}{simResult.summary.roi.toFixed(2)}%
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }} elevation={0}>
                                        <Typography variant="caption" color="text.secondary">S&P 500 Return</Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: simResult.summary.benchmark_roi >= 0 ? 'green' : 'red' }}>
                                            {simResult.summary.benchmark_roi >= 0 ? '+' : ''}{simResult.summary.benchmark_roi.toFixed(2)}%
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Paper sx={{ p: 2, bgcolor: simResult.summary.net_profit >= 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)', borderRadius: 2 }} elevation={0}>
                                        <Typography variant="caption" color="text.secondary">Net Profit ($)</Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: simResult.summary.net_profit >= 0 ? 'green' : 'red' }}>
                                            {simResult.summary.net_profit >= 0 ? '+' : ''}{simResult.summary.net_profit.toFixed(2)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }} elevation={0}>
                                        <Typography variant="caption" color="text.secondary">Alpha (vs Market)</Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: (simResult.summary.roi - simResult.summary.benchmark_roi) >= 0 ? 'green' : 'red' }}>
                                            {(simResult.summary.roi - simResult.summary.benchmark_roi).toFixed(2)}%
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }} elevation={0}>
                                        <Typography variant="caption" color="text.secondary">Buy & Hold ({simResult.ticker})</Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: simResult.summary.ticker_hold_roi >= 0 ? 'green' : 'red' }}>
                                            {simResult.summary.ticker_hold_roi >= 0 ? '+' : ''}{simResult.summary.ticker_hold_roi?.toFixed(2)}%
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }} elevation={0}>
                                        <Typography variant="caption" color="text.secondary">Alpha (vs {simResult.ticker})</Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: simResult.summary.alpha_vs_ticker >= 0 ? 'green' : 'red' }}>
                                            {simResult.summary.alpha_vs_ticker >= 0 ? '+' : ''}{simResult.summary.alpha_vs_ticker?.toFixed(2)}%
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }} elevation={0}>
                                        <Typography variant="caption" color="text.secondary">Total Trades</Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--black)' }}>
                                            <span style={{ color: 'green' }}>{simResult.summary.total_buys} B</span> / <span style={{ color: 'red' }}>{simResult.summary.total_sells} S</span>
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Box sx={{ height: 400, width: '100%' }}>
                                <ResponsiveContainer width="100%" height={400}>
                                    <ComposedChart data={simResult.chart_data}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{fontSize: 12}} 
                                            tickFormatter={(str) => {
                                                const date = new Date(`${str}T00:00:00`);
                                                return `${date.getMonth()+1}/${date.getDate()}`;
                                            }}
                                        />
                                        <YAxis unit="%" />
                                        <Tooltip 
                                            formatter={(value) => {
                                                if (typeof value === 'number') return `${value.toFixed(2)}%`;
                                                return value;
                                            }}
                                        />
                                        <Legend />
                                        <Line type="monotone" dataKey="strategy_pct" stroke="var(--pacific-cyan)" strokeWidth={3} dot={false} name="My Strategy" />
                                        <Line type="monotone" dataKey="benchmark_pct" stroke="#9e9e9e" strokeWidth={2} strokeDasharray="5 5" dot={false} name="S&P 500 (VOO)" />
                                        <Line type="monotone" dataKey="ticker_hold_pct" stroke="#ff9800" strokeWidth={2} strokeDasharray="3 3" dot={false} name={`Buy & Hold (${simResult.ticker})`} />
                                        
                                        {/* Buy/Sell Markers */}
                                        <Scatter name="Buy Signal" dataKey="buy_point" fill="#00C853" shape={<CustomShape />} />
                                        <Scatter name="Sell Signal" dataKey="sell_point" fill="#D50000" shape={<CustomShape />} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </Box>
                        </Box>
                    )}
                </Paper>
                </Box>
                </Box>

            </Container>

            <Dialog open={!!selectedStock} onClose={closeStockChart} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: 'var(--rosewood)' }}>
                    {selectedStock} - Price History & Volume
                </DialogTitle>
                <DialogContent>
                    {loadingChart ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : chartError ? (
                        <Alert severity="error">{chartError}</Alert>
                    ) : (
                        <Box sx={{ height: 500, width: '100%', mt: 2 }}>
                            <ResponsiveContainer width="100%" height={500}>
                                <ComposedChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{fontSize: 12}} 
                                        tickFormatter={(str) => {
                                            const date = new Date(`${str}T00:00:00`);
                                            return `${date.getMonth()+1}/${date.getDate()}`;
                                        }}
                                    />
                                    <YAxis yAxisId="left" domain={['auto', 'auto']} orientation="left" stroke="var(--rosewood)" />
                                    <YAxis yAxisId="right" orientation="right" stroke="var(--pacific-cyan)" />
                                    <Tooltip 
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Legend />
                                    <Bar yAxisId="right" dataKey="volume" name="Volume" opacity={0.5}>
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.close >= entry.open ? 'var(--pacific-cyan)' : 'var(--crimson)'} />
                                        ))}
                                    </Bar>
                                    <Line yAxisId="left" type="monotone" dataKey="close" stroke="var(--rosewood)" strokeWidth={2} dot={false} name="Close Price" />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeStockChart} sx={{ color: 'var(--chinese-violet)' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
